import { Text } from '@mantine/core'
import { IconDatabaseOff, IconRefresh, IconStethoscope } from '@tabler/icons-react'
import { EmptyState } from '@/components/ui'
import type { AwsAccessOverallStatus } from '@/api/awsAccess'
import { humanizeOverallStatus } from '@/api/awsAccess'

interface NoRdsEmptyStateProps {
  tenantName?: string | null
  onRetry?: () => void
  awsAccessStatus?: AwsAccessOverallStatus
  awsAccessError?: string | null
  onDiagnose?: () => void
}

export function NoRdsEmptyState({
  tenantName,
  onRetry,
  awsAccessStatus,
  awsAccessError,
  onDiagnose,
}: NoRdsEmptyStateProps) {
  const hasAccessIssue =
    awsAccessStatus && awsAccessStatus !== 'healthy' && awsAccessStatus !== 'unknown'
  const title = hasAccessIssue
    ? 'Acesso AWS indisponível para este tenant'
    : 'Nenhuma instância RDS encontrada'

  const description = hasAccessIssue ? (
    <>
      {tenantName ? (
        <>
          O ConsoleAI não conseguiu acessar a conta AWS do tenant{' '}
          <Text component="span" fw={600} c="var(--ai-brand-verde)">
            {tenantName}
          </Text>
          .
        </>
      ) : (
        <>O ConsoleAI não conseguiu acessar a conta AWS do tenant atual.</>
      )}
      <br />
      <Text component="span" fw={500}>
        {humanizeOverallStatus(awsAccessStatus!)}
      </Text>
      {awsAccessError ? (
        <>
          <br />
          <Text component="span" size="xs" c="dimmed">
            Detalhe: {awsAccessError}
          </Text>
        </>
      ) : null}
    </>
  ) : (
    <>
      {tenantName ? (
        <>
          O tenant{' '}
          <Text component="span" fw={600} c="var(--ai-brand-verde)">
            {tenantName}
          </Text>{' '}
          não tem instâncias RDS nas regiões configuradas.
        </>
      ) : (
        <>O tenant atual não tem instâncias RDS nas regiões configuradas.</>
      )}
      <br />
      Verifique a região ou contate o administrador.
    </>
  )

  return (
    <EmptyState
      icon={IconDatabaseOff}
      title={title}
      description={description}
      secondaryAction={
        onRetry
          ? { label: 'Atualizar', icon: <IconRefresh size={14} />, onClick: onRetry }
          : undefined
      }
      primaryAction={
        onDiagnose
          ? {
              label: 'Diagnosticar acesso AWS',
              icon: <IconStethoscope size={14} />,
              onClick: onDiagnose,
              permission: { resource: 'usuarios', action: 'manage' },
            }
          : undefined
      }
    />
  )
}
